
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield } from 'lucide-react';
import { useFormValidation } from '@/hooks/useFormValidation';
import { validateInput, getClientIP } from '@/utils/securityUtils';

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  message: string;
  isExistingCustomer: boolean;
};

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<ContactFormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
      isExistingCustomer: false,
    },
  });

  const { submitWithRateLimit } = useFormValidation({
    rateLimitAction: 'contact_form',
    rateLimitIdentifier: 'contact_submission', // This will be updated with IP in the submit function
    rateLimitLimit: 3,
    rateLimitWindow: 60
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    
    // Get client IP for rate limiting
    const clientIP = await getClientIP();
    
    const success = await submitWithRateLimit(
      {
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message
      },
      async (sanitizedData) => {
        // Save the message to the database
        const { error: dbError } = await (supabase as any)
          .from('contact_messages')
          .insert([{
            name: sanitizedData.name,
            email: sanitizedData.email,
            phone: sanitizedData.phone || null,
            message: sanitizedData.message,
            is_existing_customer: data.isExistingCustomer
          }]);

        if (dbError) {
          console.error('Database error:', dbError);
          throw dbError;
        }

        // Send email notification to admin
        const { error: emailError } = await supabase.functions.invoke('send-contact-notification', {
          body: {
            name: sanitizedData.name,
            email: sanitizedData.email,
            phone: sanitizedData.phone,
            message: sanitizedData.message,
            isExistingCustomer: data.isExistingCustomer
          }
        });

        if (emailError) {
          console.error('Email notification error:', emailError);
          // Don't fail the form submission if email fails, just log it
          console.warn('Message saved but email notification failed');
        }
        
        toast.success('Message sent successfully! We will get back to you soon.', {
          duration: 5000,
        });
        
        form.reset();
      }
    );

    setIsSubmitting(false);
  };

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      <h2 className="text-2xl font-bold mb-6">Contact Us</h2>
      
      {/* Money-back Guarantee Section */}
      <Card className="mb-6 border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <Shield className="h-5 w-5" />
            100% Satisfaction Guarantee
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-green-700">
            We stand behind our service! If you're not completely satisfied with our bin washing service, 
            we offer a full money-back guarantee. Contact us within 7 days of your service, and we'll 
            make it right or provide a full refund - no questions asked.
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Get in Touch</CardTitle>
          <CardDescription>
            Have questions about our bin washing service? Need to report an issue? Send us a message and we'll get back to you as soon as possible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                rules={{
                  required: "Name is required",
                  validate: (value) => validateInput.name(value) || "Name must be between 1-100 characters"
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Your name" 
                        maxLength={100}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="email"
                rules={{
                  required: "Email is required",
                  validate: (value) => validateInput.email(value) || "Please enter a valid email address"
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input 
                        type="email" 
                        placeholder="your.email@example.com" 
                        maxLength={254}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="phone"
                rules={{
                  validate: (value) => !value || validateInput.phone(value) || "Please enter a valid phone number"
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number (optional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="tel" 
                        placeholder="Your phone number" 
                        maxLength={15}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="isExistingCustomer"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox 
                        checked={field.value} 
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>I am an existing customer</FormLabel>
                    </div>
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="message"
                rules={{
                  required: "Message is required",
                  validate: (value) => validateInput.message(value) || "Message must be at least 10 characters long"
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Please describe how we can help you..." 
                        className="min-h-[150px]" 
                        maxLength={5000}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full" 
                variant="green"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
      
      {/* Social Media Contact Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Follow Us on Social Media</CardTitle>
          <CardDescription>
            Connect with us on social media for updates, tips, and quick responses to your questions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center gap-4">
            <a 
              href="https://www.facebook.com/profile.php?id=61571056657200" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </a>
            <a 
              href="https://www.instagram.com/bins.andthings" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              @bins.andthings
            </a>
          </div>
        </CardContent>
      </Card>
      
      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Contact Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="flex items-center">
                <span className="font-medium mr-2">Email:</span> 
                <a href="mailto:Admin@binsandthings.co.uk" className="text-green-600 hover:underline">
                  Admin@binsandthings.co.uk
                </a>
              </p>
              <p className="flex items-center">
                <span className="font-medium mr-2">Response Time:</span> 
                <span>Within 24 hours</span>
              </p>
              <p className="text-sm text-gray-600 mt-4">
                We're a local family-run business committed to providing excellent service to our community.
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Our Commitment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-sm">100% Satisfaction Guarantee</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-sm">Fully Insured Service</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-600" />
                <span className="text-sm">Eco-Friendly Cleaning Products</span>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                We aim to respond to all inquiries within 24 hours. For urgent matters regarding 
                your bin washing service, please email us directly.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Contact;
